import React, { useRef, useCallback, useState } from 'react';
import classes from 'classnames';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import { UserData, GroupUserData } from '@/school-management/types/user-data';
import UserRow from './UserRow/UserRow';
import AddUserRow from './AddUserRow/AddUserRow';
import classNames from './UsersTable.module.scss';
import FullClassModal from '@/components/FullClassModal/FullClassModal';

interface UsersTableProps {
  usersData: UserData[] | GroupUserData[];
  setUsersData?: React.Dispatch<React.SetStateAction<UserData[]>>;
  allowActions?: boolean;
  allowNewRows?: boolean;
  title?: string;
  header?: any;
  disableSubmitButton?: boolean;
  setDisableSubmitButton?: React.Dispatch<React.SetStateAction<boolean>>;
  showNewUserInput?: boolean;
  setShowNewUserInput?: React.Dispatch<React.SetStateAction<boolean>>;
  isModal?: boolean;
  usersInClass?: number;
  isTeacher?: boolean;
}

const UsersTable: React.FC<UsersTableProps> = ({
  usersData,
  setUsersData,
  allowActions = false,
  allowNewRows = false,
  title,
  header,
  setDisableSubmitButton,
  showNewUserInput,
  setShowNewUserInput,
  isModal,
  usersInClass = 0,
  isTeacher,
}) => {
  const { t } = useAdminTranslation();
  const [showFullClassModal, setShowFullClassModal] = useState<boolean>(false);
  const tableContainerRef = useRef<HTMLTableSectionElement>(null);

  const maxUsersNumberInClass = 50;
  const allowedUsersNumber = maxUsersNumberInClass - usersInClass;

  const handleAddNewUserRow = () => {
    // The limit on the maximum number of users only exists for students, not for teachers.
    if (usersData.length >= allowedUsersNumber && !isTeacher) {
      setShowFullClassModal(true);
      return;
    }

    if (setShowNewUserInput) {
      setShowNewUserInput(true);
      setTimeout(() => {
        handleScroll();
      }, 0);
    }
  };

  const handleScroll = useCallback(() => {
    const tableContainer = tableContainerRef.current;
    if (tableContainer) {
      tableContainer.scrollTop = tableContainer.scrollHeight + 60;
    }
  }, []);

  return (
    <>
      <div
        className={classes(classNames.component, {
          [classNames.componentModal]: isModal,
        })}
      >
        <div
          className={isModal ? `${classNames.wrapper} ${classNames.isModal}` : classNames.wrapper}
        >
          {header}
          {title && <h3 className={classNames.tableTitle}>{title}</h3>}
          <table>
            <thead>
              <tr>
                <th>{t('studentDataModal.editForm.firstname')}</th>
                <th>{t('studentDataModal.editForm.lastname')}</th>
                <th>{t('studentDataModal.editForm.email')}</th>
                {allowActions ? (
                  <th className={classNames.actionIcons}></th>
                ) : (
                  <th>{isTeacher ? t('editClassTeacherStatus') : t('editClassStudentStatus')}</th>
                )}
              </tr>
            </thead>
            <tbody ref={tableContainerRef}>
              {usersData.length > 0 &&
                usersData.map((user, index) => (
                  <UserRow
                    key={`user-row--${index}`}
                    user={user}
                    index={index}
                    allowActions={allowActions}
                    usersData={usersData}
                    setUsersData={setUsersData}
                    setDisableSubmitButton={setDisableSubmitButton}
                  />
                ))}
              {showNewUserInput && setShowNewUserInput && setUsersData && (
                <AddUserRow setUsersData={setUsersData} setShowNewUserInput={setShowNewUserInput} />
              )}
            </tbody>
          </table>
        </div>
        {allowNewRows && (
          <div className={classNames.addUser}>
            <button
              onClick={() => handleAddNewUserRow()}
              aria-label={
                isTeacher
                  ? t('settings.add_teachers.add_teacher').toString()
                  : t('settings.add_students.add_student').toString()
              }
            >
              +
              {isTeacher
                ? t('settings.add_teachers.add_teacher')
                : t('settings.add_students.add_student')}
            </button>
          </div>
        )}
      </div>
      <FullClassModal
        show={showFullClassModal}
        onClose={() => {
          setShowFullClassModal(false);
        }}
      />
    </>
  );
};

export default UsersTable;
